/**
 * 指導計画 (teaching plan)
 *
 * 契約: chaild-api の docs/architecture/teaching-plan/api-design.md (契約v2)
 * ID規約: planId / commentId / classId / templateId / phraseId は encode済みの文字列。
 *         nurseryId・userNursery系ID (承認者ID・authorUserNurseryId) は encode済みの number。
 */
import ApiClient from "@/api/ApiClient";
import {
  BatchStreamLogResponse,
  NurseryCreateTeachingPlanCommentRequestBody,
  NurseryCreateTeachingPlanPhraseRequestBody,
  NurseryCreateTeachingPlanRequestBody,
  NurseryCreateTeachingPlanTemplateRequestBody,
  NurseryDeleteTeachingPlanPhraseResponse,
  NurseryDeleteTeachingPlanTemplateResponse,
  NurseryExportTeachingPlanExcelResponse,
  NurseryExportTeachingPlanPhrasesResponse,
  NurseryTeachingPlanApproverSettingListResponse,
  NurseryTeachingPlanCommentListResponse,
  NurseryTeachingPlanDetailResponse,
  NurseryTeachingPlanListResponse,
  NurseryTeachingPlanListTabType,
  NurseryTeachingPlanPastValuesResponse,
  NurseryTeachingPlanPhraseImportResponse,
  NurseryTeachingPlanPhraseImportUrlResponse,
  NurseryTeachingPlanPhraseListResponse,
  NurseryTeachingPlanPhrasesResponse,
  NurseryTeachingPlanTemplateListResponse,
  NurseryTeachingPlanTemplateResponse,
  NurseryTeachingPlanTransferCandidateListResponse,
  NurseryTeachingPlanTransferRuleListResponse,
  NurseryTeachingPlanType,
  NurseryTransferTeachingPlanResponse,
  NurseryUpdateTeachingPlanPhraseRequestBody,
  NurseryUpdateTeachingPlanTemplateRequestBody,
  NurseryUpsertTeachingPlanApproverSettingsRequestBody,
  NurseryUpsertTeachingPlanTransferRulesRequestBody,
  NurseryUpsertTeachingPlanValuesRequestBody,
  NurseryUpsertTeachingPlanValuesResponse,
} from "chaild-api/lib";

export default class ApiTeachingPlan {
  /**
   * 帳票 (§5.1)
   */

  /** #1 一覧 + ステータスタブ件数 */
  public static async listPlans(input: {
    nurseryId: number;
    tab?: NurseryTeachingPlanListTabType;
    planType?: NurseryTeachingPlanType | "";
    classId?: string | null;
    businessYear?: number | null;
    authorUserNurseryId?: number | null;
    keyword?: string;
    limit?: number;
    skip?: number;
  }) {
    const response = await ApiClient.get("/teaching-plan", input);
    return response as NurseryTeachingPlanListResponse;
  }

  /** #2 作成 (空 or 上位計画から転記)。応答は作成された帳票の詳細 */
  public static async createPlan(input: NurseryCreateTeachingPlanRequestBody) {
    const response = await ApiClient.post("/teaching-plan", input);
    return response as NurseryTeachingPlanDetailResponse;
  }

  /** #3 詳細 (レイアウト + 値 + 参照解決 + コメント集計 + 権限) */
  public static async findPlan(input: { planId: string }) {
    const response = await ApiClient.get(`/teaching-plan/${input.planId}`, null);
    return response as NurseryTeachingPlanDetailResponse;
  }

  /** #4 削除 (下書き・差戻しのみ) */
  public static async deletePlan(input: { planId: string }) {
    const response = await ApiClient.delete(`/teaching-plan/${input.planId}`, {});
    return response as { planId: string };
  }

  /** #5 セル値upsert (自動保存)。1リクエスト50件まで */
  public static async upsertValues(
    input: { planId: string } & NurseryUpsertTeachingPlanValuesRequestBody
  ) {
    // 自動保存は裏で走るため、失敗時にモーダルを出さず画面内の表示に任せる
    const response = await ApiClient.put(
      `/teaching-plan/${input.planId}/values`,
      { values: input.values },
      { silentError: true }
    );
    return response as NurseryUpsertTeachingPlanValuesResponse;
  }

  /**
   * ワークフロー (§5.2)。いずれも応答は最新の詳細
   */

  /** #6 提出 / 再提出 (現ステータスで自動判別) */
  public static async submitPlan(input: { planId: string }) {
    const response = await ApiClient.post(`/teaching-plan/${input.planId}/submit`, {});
    return response as NurseryTeachingPlanDetailResponse;
  }

  /** #7 承認 (一次 / 最終は自動判別) */
  public static async approvePlan(input: { planId: string }) {
    const response = await ApiClient.post(`/teaching-plan/${input.planId}/approve`, {});
    return response as NurseryTeachingPlanDetailResponse;
  }

  /** #8 差戻し */
  public static async returnPlan(input: { planId: string; reason: string }) {
    const response = await ApiClient.post(`/teaching-plan/${input.planId}/return`, {
      reason: input.reason,
    });
    return response as NurseryTeachingPlanDetailResponse;
  }

  /**
   * 転記 (§5.3)
   */

  /** #9 一括転記モーダル用の対比一覧 */
  public static async listTransferCandidates(input: { planId: string }) {
    const response = await ApiClient.get(
      `/teaching-plan/${input.planId}/transfer-candidates`,
      null
    );
    return response as NurseryTeachingPlanTransferCandidateListResponse;
  }

  /** #10 転記実行 (単項目=1件、一括=複数) */
  public static async transfer(input: {
    planId: string;
    itemKeys: string[];
    overwrite: boolean;
  }) {
    const response = await ApiClient.post(`/teaching-plan/${input.planId}/transfer`, {
      itemKeys: input.itemKeys,
      overwrite: input.overwrite,
    });
    return response as NurseryTransferTeachingPlanResponse;
  }

  /**
   * コメント (§5.4)
   */

  /** #11 全スレッド取得 */
  public static async listComments(input: { planId: string }) {
    const response = await ApiClient.get(`/teaching-plan/${input.planId}/comments`, null);
    return response as NurseryTeachingPlanCommentListResponse;
  }

  /** #12 投稿 (起点 or 返信)。承認者投稿は確認中への自動遷移を伴う */
  public static async createComment(
    input: { planId: string } & NurseryCreateTeachingPlanCommentRequestBody
  ) {
    const response = await ApiClient.post(`/teaching-plan/${input.planId}/comments`, {
      itemKey: input.itemKey,
      body: input.body,
      parentCommentId: input.parentCommentId,
    });
    return response as NurseryTeachingPlanCommentListResponse;
  }

  /** #13 解決 / 再オープン (起点コメントのみ) */
  public static async resolveComment(input: {
    planId: string;
    commentId: string;
    action: "resolve" | "reopen";
  }) {
    const response = await ApiClient.put(
      `/teaching-plan/${input.planId}/comments/${input.commentId}`,
      { action: input.action }
    );
    return response as NurseryTeachingPlanCommentListResponse;
  }

  /**
   * 入力支援 (§5.5)
   */

  /** #14 過去帳票の同一項目値 (同クラス優先 → 期間降順) */
  public static async listPastValues(input: {
    nurseryId: number;
    planId: string;
    itemKey: string;
    limit?: number;
  }) {
    const response = await ApiClient.get("/teaching-plan/past-values", input);
    return response as NurseryTeachingPlanPastValuesResponse;
  }

  /** #15 エディタの文例パネル用 (レイアウト × phraseKey で引く) */
  public static async listCellPhrases(input: {
    nurseryId: number;
    templateId: string;
    phraseKey: string;
    keyword?: string;
  }) {
    const response = await ApiClient.get("/teaching-plan/phrases", input);
    return response as NurseryTeachingPlanPhrasesResponse;
  }

  /**
   * 出力 (§5.6)
   */

  /** #16 単一Excel出力 (同期・presigned URL) */
  public static async exportExcel(input: { planId: string }) {
    const response = await ApiClient.post(`/teaching-plan/${input.planId}/export-excel`, {});
    return response as NurseryExportTeachingPlanExcelResponse;
  }

  /**
   * #17 複数帳票の一括Excel出力 (非同期ZIP)。
   * 応答は作成された batch_stream_log。完了は GET /batch-stream-log をポーリングして確認する
   */
  public static async bulkExportExcel(input: { nurseryId: number; planIds: string[] }) {
    const response = await ApiClient.post("/teaching-plan/export-excel", input);
    return response as BatchStreamLogResponse;
  }

  /**
   * レイアウト (§5.7・管理者/施設長のみ。#18の一覧のみ全ロール)
   */

  /** #18 レイアウト一覧 (標準レイアウトの遅延シードもここで行われる) */
  public static async listTemplates(input: {
    nurseryId: number;
    planType?: NurseryTeachingPlanType | "";
  }) {
    const response = await ApiClient.get("/teaching-plan-template", input);
    return response as NurseryTeachingPlanTemplateListResponse;
  }

  /** #24 レイアウト取得 (最新公開版 + draft) */
  public static async findTemplate(input: { templateId: string }) {
    const response = await ApiClient.get(`/teaching-plan-template/${input.templateId}`, null);
    return response as NurseryTeachingPlanTemplateResponse;
  }

  /** #25 レイアウト作成 (copyFromTemplateId 指定で複製)。作成直後は未公開 */
  public static async createTemplate(input: NurseryCreateTeachingPlanTemplateRequestBody) {
    const response = await ApiClient.post("/teaching-plan-template", input);
    return response as NurseryTeachingPlanTemplateResponse;
  }

  /** #19 draft保存・リネーム (両方の省略は不可) */
  public static async updateTemplate(input: NurseryUpdateTeachingPlanTemplateRequestBody) {
    const response = await ApiClient.put("/teaching-plan-template", input);
    return response as NurseryTeachingPlanTemplateResponse;
  }

  /** #20 公開 (版+1・転記ルールの要確認フラグ再計算) */
  public static async publishTemplate(input: { templateId: string }) {
    const response = await ApiClient.post("/teaching-plan-template/publish", input);
    return response as NurseryTeachingPlanTemplateResponse;
  }

  /** #26 レイアウト削除 (使用中は不可)。転記ルール・文例も連鎖削除 */
  public static async deleteTemplate(input: { templateId: string }) {
    const response = await ApiClient.delete(`/teaching-plan-template/${input.templateId}`, {});
    return response as NurseryDeleteTeachingPlanTemplateResponse;
  }

  /**
   * 転記ルール (§5.7 #21/#22)
   */

  /** #21 転記ルール一覧 (targetTemplateId 省略で園の全ルール) */
  public static async listTransferRules(input: {
    nurseryId: number;
    targetTemplateId?: string;
  }) {
    const response = await ApiClient.get("/teaching-plan-transfer-rule", input);
    return response as NurseryTeachingPlanTransferRuleListResponse;
  }

  /** #22 転記先レイアウト単位の一括置換 */
  public static async upsertTransferRules(
    input: NurseryUpsertTeachingPlanTransferRulesRequestBody
  ) {
    const response = await ApiClient.put("/teaching-plan-transfer-rule", input);
    return response as NurseryTeachingPlanTransferRuleListResponse;
  }

  /**
   * 承認者・提出期限設定 (§5.7 #23)
   */

  /** #23 取得 */
  public static async listApproverSettings(input: { nurseryId: number }) {
    const response = await ApiClient.get("/teaching-plan-approver-setting", input);
    return response as NurseryTeachingPlanApproverSettingListResponse;
  }

  /** #23 園単位の一括置換 */
  public static async upsertApproverSettings(
    input: NurseryUpsertTeachingPlanApproverSettingsRequestBody
  ) {
    const response = await ApiClient.put("/teaching-plan-approver-setting", input);
    return response as NurseryTeachingPlanApproverSettingListResponse;
  }

  /**
   * 文例の管理 (§5.8・管理者/施設長のみ)
   */

  /** #27 項目ごとにグルーピングした一覧 (文例0件の項目も空配列で返る) */
  public static async listPhrases(input: {
    nurseryId: number;
    templateId: string;
    phraseKey?: string;
    keyword?: string;
  }) {
    const response = await ApiClient.get("/teaching-plan-phrase", input);
    return response as NurseryTeachingPlanPhraseListResponse;
  }

  /** #28 追加 */
  public static async createPhrase(input: NurseryCreateTeachingPlanPhraseRequestBody) {
    const response = await ApiClient.post("/teaching-plan-phrase", input);
    return response as NurseryTeachingPlanPhraseListResponse;
  }

  /** #29 更新 (本文・並び順) */
  public static async updatePhrase(
    input: { phraseId: string } & NurseryUpdateTeachingPlanPhraseRequestBody
  ) {
    const response = await ApiClient.put(`/teaching-plan-phrase/${input.phraseId}`, {
      body: input.body,
      sortOrder: input.sortOrder,
    });
    return response as NurseryTeachingPlanPhraseListResponse;
  }

  /** #30 削除 (論理削除) */
  public static async deletePhrase(input: { phraseId: string }) {
    const response = await ApiClient.delete(`/teaching-plan-phrase/${input.phraseId}`, {});
    return response as NurseryDeleteTeachingPlanPhraseResponse;
  }

  /** #31 CSVエクスポート (同期・presigned URL) */
  public static async exportPhrases(input: { nurseryId: number; templateId: string }) {
    const response = await ApiClient.post("/teaching-plan-phrase/export", input);
    return response as NurseryExportTeachingPlanPhrasesResponse;
  }

  /** #33 CSVのアップロード先を払い出す (presigned PUT URL + fileKey) */
  public static async createPhraseImportUrl(input: {
    nurseryId: number;
    templateId: string;
  }) {
    const response = await ApiClient.post("/teaching-plan-phrase/import-url", input);
    return response as NurseryTeachingPlanPhraseImportUrlResponse;
  }

  /** #32 CSVインポート (同期・#33で払い出した fileKey を指定)。既存は消さず追記 */
  public static async importPhrases(input: {
    nurseryId: number;
    templateId: string;
    fileKey: string;
  }) {
    const response = await ApiClient.post("/teaching-plan-phrase/import", input);
    return response as NurseryTeachingPlanPhraseImportResponse;
  }
}
